import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useIsFetching } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { locationsAccountAtom, locationsSelectedFeatureIdAtom } from 'src/atoms/filters/locations';
import { LocationsMap } from 'src/locations/LocationsMap';
import { LocationsMapDisclaimer } from 'src/locations/LocationsMapDisclaimer';
import { LocationsMapSelectedOverlay } from 'src/locations/LocationsMapSelectedOverlay';

export const LocationsMapSection: FC = () => {
  const { t } = useTranslation();

  const account = useAtomValue(locationsAccountAtom);
  const selectedImsi = useAtomValue(locationsSelectedFeatureIdAtom);

  const isFetchingMap = useIsFetching({
    predicate(query) {
      const hasMeta = query.meta !== undefined;
      const isLocationsMap = hasMeta && query.meta.isLocationsMap === true;
      return isLocationsMap;
    },
  });

  return (
    <Card data-testid="locations.mapSection" sx={{ height: '100%' }}>
      <CardContent sx={{ height: '100%' }}>
        <Stack height="100%" spacing={10} direction="column" useFlexGap>
          <Box flexShrink={0} flexGrow={0}>
            <Typography
              data-testid="locations.mapSection.title"
              variant="h3"
              color="text.secondary"
              gutterBottom
            >
              {t('locations.map.title')}
            </Typography>
            {account ? null : (
              <Typography variant="text1" color="textSecondary" gutterBottom>
                {t('locations.map.selectAnAccount')}
              </Typography>
            )}
          </Box>
          {account ? (
            <Fragment>
              <Box
                minHeight="500px"
                borderRadius={(theme) => theme.shape.borderRadius}
                overflow="hidden"
                position="relative"
                flexShrink={0}
                flexGrow={1}
              >
                {isFetchingMap ? (
                  <LinearProgress
                    aria-label="Loading…"
                    color="grey"
                    sx={{ top: 0, left: 0, width: '100%', position: 'absolute', zIndex: 1 }}
                  />
                ) : null}
                <Stack
                  data-testid="locations.map.deviceDetails"
                  position="absolute"
                  top={0}
                  right={0}
                  height="100%"
                  width="100%"
                  alignItems="center"
                  justifyContent="end"
                  padding={10}
                >
                  <Card
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      paddingX: 20,
                      paddingY: 12,
                      width: '100%',
                      height: '80%',
                      backgroundColor: 'white',
                      borderRadius: theme.shape.borderRadius,
                      zIndex: 20,
                      transform: selectedImsi ? 'translateX(0)' : 'translateX(150%)',
                      transitionProperty: 'transform',
                      transitionDuration: '0.4s',
                      transitionTimingFunction: 'ease',
                    })}
                  >
                    {selectedImsi ? (
                      <LocationsMapSelectedOverlay selectedImsi={selectedImsi} />
                    ) : null}
                  </Card>
                </Stack>
                <LocationsMap />
              </Box>
              <Box flexShrink={0} flexGrow={0}>
                <LocationsMapDisclaimer testId="locations.mapSection.disclaimer" />
              </Box>
            </Fragment>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};
